import { Close } from '@mui/icons-material';
import {
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Grid2 as Grid,
  CircularProgress,
  Box,
  IconButton,
  Link,
} from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { Location, useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';

import { DividerFaded } from '@/components/common/DividerFaded';
import { FullScreenContainer } from '@/components/common/FullScreenContainer';
import { LoadingIndicator } from '@/components/common/LoadingIndicator';
import TopNavBar from '@/components/navigation/TopNavBar';
import {
  UpdateUserProfileForm,
  UpdateUserProfileFormRef,
} from '@/components/user/UpdateUserProfileForm';
import { useAuthContext } from '@/context/AuthProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useLogger } from '@/hooks/useLogger';
import { useRequestAccountDeletion } from '@/hooks/user/useRequestAccountDeletion';
import { useUserProfile } from '@/hooks/user/useUserProfile';
import { useTranslationPrefix } from '@/translations/i18n';
export interface UpdateUserProfileLocationState {
  hideTopNav?: boolean;
  redirectToHomeOnProfileUpdate?: boolean;
}

const UpdateUserProfile: React.FC = () => {
  const t = useTranslationPrefix('update_profile');
  const navigate = useNavigate();
  const location = useLocation() as Location<UpdateUserProfileLocationState | undefined>;
  const hideTopNav = location.state?.hideTopNav;
  const redirectToHomeOnProfileUpdate = location.state?.redirectToHomeOnProfileUpdate ?? true;
  const { user } = useAuthContext();
  const { showAlert } = useSnackbar();
  const logger = useLogger('UpdateUserProfile');
  const {
    userProfile,
    isInitialFetchingUserProfile,
    isFetchingUserProfile,
    updateUserProfile,
    isUpdatingUserProfile,
    isUserProfileUpdated,
    updateUserProfileError,
  } = useUserProfile();
  const { requestAccountDeletion, isPending: isRequestingAccountDeletion } =
    useRequestAccountDeletion();
  const updateUserProfileFormRef = useRef<UpdateUserProfileFormRef>(null);
  const handleRequestAccountDeletion = async () => {
    try {
      await requestAccountDeletion();
      showAlert(t('close_account_success'), 'success');
    } catch (error: any) {
      logger.error('Account deletion request error:', error.message);
      showAlert(t('close_account_error'), 'error');
    }
  };

  const handleCloseClick = () => {
    navigate('/');
  };

  useEffect(() => {
    if (!isUpdatingUserProfile && isUserProfileUpdated && redirectToHomeOnProfileUpdate) {
      navigate('/');
    }
  }, [isUpdatingUserProfile, isUserProfileUpdated, redirectToHomeOnProfileUpdate]);

  if (isInitialFetchingUserProfile) {
    return (
      <FullScreenContainer transparentBackground={!hideTopNav}>
        {!hideTopNav && <TopNavBar />}
        <Stack flex={1} alignItems={'center'} justifyContent="center">
          <CircularProgress sx={{ color: 'background.paper' }} />
        </Stack>
      </FullScreenContainer>
    );
  }

  return (
    <FullScreenContainer
      transparentBackground={!hideTopNav}
      sx={{
        gap: { sm: 5, md: 10 },
      }}>
      {!hideTopNav && <TopNavBar />}
      <Box
        sx={(theme) => ({
          width: { sm: `calc(100% + ${theme.spacing(6)})`, md: '100%' },
          maxWidth: { md: 800 },
          flexGrow: 1,
          alignItems: 'center',
          backgroundColor: { sm: 'background.paper', md: 'transparent' },
        })}>
        <Card>
          <CardContent sx={{ gap: 5, position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 26, right: 10 }}>
              <IconButton color="primary" onClick={handleCloseClick}>
                <Close fontSize="inherit" />
              </IconButton>
            </Box>

            <Stack spacing={2.5}>
              <Typography variant="h5" color="text.secondary">
                {t('title')}
              </Typography>
              <DividerFaded />
            </Stack>

            <Typography variant="h4" color="text.secondary" sx={{ pt: 1.5 }}>
              {t('greeting', { name: user?.firstName })}
            </Typography>

            <UpdateUserProfileForm
              ref={updateUserProfileFormRef}
              values={userProfile}
              onSubmit={(values) => updateUserProfile(values, { markUserProfileAsComplete: true })}
            />

            <Stack spacing={{ sm: 6, md: 3 }}>
              <Grid container columns={{ sm: 1, md: 2 }} columnSpacing={9} rowSpacing={5}>
                <Grid size={1}>
                  <Button
                    fullWidth
                    loading={isUpdatingUserProfile || isFetchingUserProfile}
                    onClick={() => updateUserProfileFormRef.current?.submitForm()}
                    data-testid="update-profile-submit-btn">
                    {user?.isProviderUser ? t('continue') : t('submit')}
                  </Button>
                </Grid>
              </Grid>

              <Grid container columns={{ sm: 1, md: 2 }} columnSpacing={9} rowSpacing={2}>
                <Grid size={1}>
                  <CloseAccount
                    onSendRequestClick={handleRequestAccountDeletion}
                    isLoading={isRequestingAccountDeletion}
                  />
                </Grid>
              </Grid>

              {updateUserProfileError && (
                <Typography variant="body2" color="error" textAlign="center">
                  {updateUserProfileError}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </FullScreenContainer>
  );
};

const CloseAccount = ({
  onSendRequestClick,
  isLoading,
}: {
  onSendRequestClick: () => void;
  isLoading?: boolean;
}) => {
  const t = useTranslationPrefix('close_account');

  return (
    <Stack alignItems="center">
      {isLoading ? (
        <LoadingIndicator size={16} />
      ) : (
        <Typography variant="body3" color="text.secondary" textAlign="center">
          {t('want_to_close_account')} <Link onClick={onSendRequestClick}>{t('send_request')}</Link>
        </Typography>
      )}
    </Stack>
  );
};

export default UpdateUserProfile;
